import { Fragment } from "react";
import ParticipantQuotes from "../components/ParticipantQuotes";
import ContentBlocks from "../components/ContentBlocks";
import Accordion from "../components/Accordion";
import ButtonLink from "../components/ButtonLink";
import CareersForm from "../components/CareersForm";
import InterestForm from "../components/InterestForm";
import Media from "../components/Media";
import SquarespaceMirror from "../components/SquarespaceMirror";
import inlineEmbeds from "../data/inline-embeds.json";
import inlineVideos from "../data/inline-videos.json";
import layoutHeights from "../data/layout-heights.json";
import {
  IOS_APP_DESTINATION_URL,
  IOS_BANNER_BACKGROUND_URL,
  IOS_LAUNCH_ANCHOR_ID,
  WEB_VIEWER_URL,
} from "../data/platform-links";
import squarespaceMirrors from "../data/squarespace-mirrors.json";
import { localAssetPath } from "../utils/assets";
import "./MappedPage.css";

const sectionClass = (theme = "") => {
  const normalized = theme.toLowerCase();
  if (normalized.includes("bright")) return "section--accent";
  if (normalized.includes("light-bold")) return "section--light-bold-legacy";
  if (normalized.includes("light")) return "section--soft";
  if (normalized.includes("white-bold")) return "section--white-bold";
  if (normalized.includes("black-bold")) return "section--black-bold";
  if (normalized.includes("white")) return "section--white";
  if (normalized.includes("dark")) return "section--dark";
  return "";
};

const sectionLayoutClasses = (className = "") => {
  const classes = [];
  const height = className.match(/section-height--([a-z-]+)/i)?.[1];
  if (height) classes.push(`mapped-section--height-${height}`);
  if (className.includes("horizontal-alignment--center")) {
    classes.push("mapped-section--align-center");
  }
  if (className.includes("vertical-alignment--middle")) {
    classes.push("mapped-section--valign-middle");
  }
  if (className.includes("has-background")) {
    classes.push("mapped-section--has-background");
  }
  return classes;
};

const backgroundVideoFor = (path, sectionIndex) => {
  if (path === "/educators" && sectionIndex === 0) {
    return "/assets/original/video/10k-science-1920x1080.mp4";
  }
  return "";
};

const buttonVariant = (button, theme = "") => {
  const normalizedTheme = theme.toLowerCase();
  const isSecondary = /secondary/i.test(button.className || "");
  const isLightSurface =
    normalizedTheme.includes("bright") ||
    normalizedTheme.includes("light") ||
    normalizedTheme.includes("white");

  if (isSecondary) {
    if (normalizedTheme.includes("white")) return "outline-accent-dark";
    return isLightSurface ? "outline-dark" : "outline";
  }
  if (normalizedTheme.includes("bright")) return "dark-accent";
  if (normalizedTheme === "white") return "accent-dark";
  if (isLightSurface) return "dark";
  return "primary";
};

const isLogoAsset = (item) =>
  /(?:logo|lockup|symbol|500logo|berkeley|stevens|institute)/i.test(
    `${item.alt || ""} ${item.src || ""}`,
  );

const normalizedText = (value = "") =>
  value.replace(/\s+/g, " ").trim();

function IosLaunchBanner() {
  return (
    <section
      aria-label="iOS app announcement"
      className="ios-launch-banner"
      id={IOS_LAUNCH_ANCHOR_ID}
      style={{
        "--ios-banner-background": `url("${IOS_BANNER_BACKGROUND_URL}")`,
      }}
    >
      <div className="content-width ios-launch-banner__inner">
        <div className="ios-launch-banner__copy">
          <p className="ios-launch-banner__eyebrow">New platform</p>
          <p className="ios-launch-banner__title">
            Now available on iPhone and iPad!
          </p>
          <p className="ios-launch-banner__text">
            Explore interactive 3D science experiences with touch-first
            navigation, intelligent audio guides, and the same science library
            across iOS, web, and Meta Quest.
          </p>
          <div className="ios-launch-banner__actions">
            <ButtonLink
              className="ios-launch-banner__button"
              href={IOS_APP_DESTINATION_URL}
              size="small"
            >
              Get it on App Store
            </ButtonLink>
            <ButtonLink
              className="ios-launch-banner__button"
              href={WEB_VIEWER_URL}
              size="small"
              target="_blank"
              variant="outline"
            >
              Open web viewer
            </ButtonLink>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContentBlocksWithEmbeds({ blocks, embeds }) {
  if (!embeds.length) return <ContentBlocks blocks={blocks} />;

  const atPosition = new Map();
  for (const embed of embeds) {
    const afterIndex = embed.insertAfter?.text
      ? blocks.findIndex(
          (block) =>
            normalizedText(block.text) ===
            normalizedText(embed.insertAfter.text),
        )
      : -1;
    const beforeIndex = embed.insertBefore?.text
      ? blocks.findIndex(
          (block) =>
            normalizedText(block.text) ===
            normalizedText(embed.insertBefore.text),
        )
      : -1;
    const position =
      afterIndex >= 0
        ? afterIndex + 1
        : beforeIndex >= 0
          ? beforeIndex
          : blocks.length;
    atPosition.set(position, [
      ...(atPosition.get(position) || []),
      embed,
    ]);
  }

  const output = [];
  let chunkStart = 0;
  for (let position = 0; position <= blocks.length; position += 1) {
    const positionEmbeds = atPosition.get(position);
    if (!positionEmbeds) continue;
    if (position > chunkStart) {
      output.push(
        <ContentBlocks
          blocks={blocks.slice(chunkStart, position)}
          key={`blocks-${chunkStart}-${position}`}
        />,
      );
    }
    positionEmbeds.forEach((embed) => {
      output.push(
        <Media
          allow={embed.allow}
          allowFullScreen={embed.allowFullScreen}
          alt={embed.title}
          className="mapped-inline-embed"
          height={embed.height}
          key={embed.id}
          referrerPolicy={embed.referrerPolicy}
          src={embed.src}
          type="iframe"
          width={embed.width}
        />,
      );
    });
    chunkStart = position;
  }
  if (chunkStart < blocks.length) {
    output.push(
      <ContentBlocks
        blocks={blocks.slice(chunkStart)}
        key={`blocks-${chunkStart}-${blocks.length}`}
      />,
    );
  }
  return output;
}

function MappedPageContent({ page }) {
  if (!page) {
    return (
      <section className="mapped-page__missing section">
        <div className="content-width">
          <h1>Page not found</h1>
        </div>
      </section>
    );
  }

  const pageSlug =
    page.path === "/"
      ? "home"
      : page.path
          .replace(/^\/+|\/+$/g, "")
          .replaceAll("/", "--")
          .replace(/[^a-z0-9-]/gi, "-");
  const measuredLayout = layoutHeights[page.path];

  return (
    <article
      className={`mapped-page mapped-page--${page.pageType} mapped-page--${pageSlug}`}
    >
      {page.sections.map((section, sectionIndex) => {
        const visibleMedia = section.media.filter(
          (item) =>
            item.src &&
            (!section.backgroundImage || item.src !== section.backgroundImage),
        );
        const authoredInlineMedia = inlineVideos
          .filter(
            (item) =>
              item.route === page.path &&
              item.sectionIndex === sectionIndex,
          )
          .sort(
            (a, b) =>
              (a.mobileVisualOrder ??
                a.desktopVisualOrder ??
                a.authoredMediaOrder ??
                0) -
              (b.mobileVisualOrder ??
                b.desktopVisualOrder ??
                b.authoredMediaOrder ??
                0),
          );
        const sectionEmbeds = inlineEmbeds
          .filter(
            (item) =>
              item.route === page.path &&
              item.sectionIndex === sectionIndex,
          )
          .sort(
            (a, b) =>
              (a.mobileVisualOrder ?? a.desktopVisualOrder ?? 0) -
              (b.mobileVisualOrder ?? b.desktopVisualOrder ?? 0),
          );
        const allVisibleMedia = [
          ...visibleMedia,
          ...authoredInlineMedia,
        ];
        const themeClass = sectionClass(section.theme);
        const isHero = sectionIndex === 0;
        const isSplit =
          allVisibleMedia.length === 1 && section.blocks.length <= 12;
        const buttons = section.links.filter((link) => link.isButton);
        const accordionItems = (section.accordions || []).map((item) => ({
          title: item.title,
          body: [item.body],
        }));
        const accordionBlockTexts = new Set(
          accordionItems.map((item) =>
            normalizedText(`${item.title} ${item.body[0]}`),
          ),
        );
        const firstAccordionIndex = section.blocks.findIndex(
          (block) =>
            block.type === "listItem" &&
            accordionBlockTexts.has(normalizedText(block.text)),
        );
        const contentBlocks = section.blocks.filter(
          (block) =>
            !(
              block.type === "listItem" &&
              accordionBlockTexts.has(normalizedText(block.text))
            ),
        );
        const blocksBeforeAccordion =
          firstAccordionIndex < 0
            ? contentBlocks
            : section.blocks
                .slice(0, firstAccordionIndex)
                .filter(
                  (block) =>
                    !accordionBlockTexts.has(normalizedText(block.text)),
                );
        const blocksAfterAccordion =
          firstAccordionIndex < 0
            ? []
            : section.blocks
                .slice(firstAccordionIndex)
                .filter(
                  (block) =>
                    !accordionBlockTexts.has(normalizedText(block.text)),
                );
        const backgroundImage = section.backgroundImage
          ? localAssetPath(section.backgroundImage)
          : "";
        const backgroundVideo = backgroundVideoFor(page.path, sectionIndex);
        const hasCareersForm =
          page.path === "/careers" && sectionIndex === 3;
        const hasSupportForm =
          page.path === "/support" && sectionIndex === 0;
        const desktopHeight =
          measuredLayout?.desktop?.[sectionIndex];
        const mobileHeight =
          measuredLayout?.mobile?.[sectionIndex];
        const sectionStyle = {
          ...(backgroundImage
            ? { "--mapped-background-image": `url("${backgroundImage}")` }
            : {}),
          ...(desktopHeight
            ? {
                "--mapped-section-desktop-min-height": `min(${(
                  (desktopHeight / 1440) *
                  100
                ).toFixed(4)}vw, ${desktopHeight}px)`,
              }
            : {}),
          ...(mobileHeight
            ? {
                "--mapped-section-mobile-min-height": `${(
                  (mobileHeight / 390) *
                  100
                ).toFixed(4)}vw`,
              }
            : {}),
        };

        return (
          <Fragment key={section.id || sectionIndex}>
          <section
            className={[
              "section",
              "mapped-section",
              themeClass,
              ...sectionLayoutClasses(section.className),
              isHero ? "mapped-section--hero" : "",
              isSplit ? "mapped-section--split" : "",
              backgroundVideo ? "mapped-section--has-background" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            key={section.id || sectionIndex}
            style={sectionStyle}
          >
            {backgroundVideo && (
              <video
                aria-hidden="true"
                autoPlay
                className="mapped-section__background-video"
                loop
                muted
                playsInline
                src={backgroundVideo}
              />
            )}
            <div className="content-width mapped-section__inner">
              <div className="mapped-section__copy rich-copy">
                {accordionItems.length > 0 ? (
                  <>
                    <ContentBlocks blocks={blocksBeforeAccordion} />
                    <Accordion items={accordionItems} />
                    <ContentBlocks blocks={blocksAfterAccordion} />
                  </>
                ) : (
                  <ContentBlocksWithEmbeds
                    blocks={contentBlocks}
                    embeds={sectionEmbeds}
                  />
                )}
                {buttons.length > 0 && (
                  <div className="button-row mapped-section__buttons">
                    {buttons.map((button) => (
                      <ButtonLink
                        href={button.href}
                        key={`${button.text}-${button.href}`}
                        variant={buttonVariant(button, section.theme)}
                      >
                        {button.text}
                      </ButtonLink>
                    ))}
                  </div>
                )}
              </div>

              {hasCareersForm && <CareersForm />}
              {hasSupportForm && <InterestForm variant="support" />}

              {allVisibleMedia.length > 0 && (
                <div
                  className={`mapped-section__media mapped-section__media--${Math.min(
                    allVisibleMedia.length,
                    4,
                  )}`}
                >
                  {allVisibleMedia.map((item) => (
                    <Media
                      alt={item.alt}
                      allow={item.allow}
                      allowFullScreen={item.allowFullScreen}
                      autoPlay={item.autoplay}
                      contain={isLogoAsset(item)}
                      controls={item.controls}
                      height={item.height}
                      key={item.id || item.src}
                      loop={item.loop}
                      muted={item.muted}
                      poster={item.poster}
                      referrerPolicy={item.referrerPolicy}
                      src={
                        item.type === "iframe"
                          ? item.src
                          : localAssetPath(item.src)
                      }
                      type={item.type}
                      width={item.width}
                    />
                  ))}
                </div>
              )}
            </div>
          </section>
          {(page.path === "/" || page.path === "/home") && sectionIndex === 0 && <ParticipantQuotes />}
          </Fragment>
        );
      })}
    </article>
  );
}

export default function MappedPage({ page }) {
  const mirrorSource = page ? squarespaceMirrors[page.path] : "";
  const fallback = <MappedPageContent page={page} />;
  const isHomePage = page?.path === "/" || page?.path === "/home";

  const renderWithLaunchBanner = (content) =>
    isHomePage ? (
      <>
        <IosLaunchBanner />
        {content}
      </>
    ) : (
      content
    );

  // Unit tests exercise the deterministic JSON-driven semantic rendering.
  // Browser QA covers the production mirror and shadow-root runtime.
  if (process.env.NODE_ENV === "test") {
    return renderWithLaunchBanner(fallback);
  }
  if (!mirrorSource) return renderWithLaunchBanner(fallback);

  return renderWithLaunchBanner(
    <SquarespaceMirror
      fallback={fallback}
      page={page}
      src={mirrorSource}
    />,
  );
}
